import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, ArrowLeftRight, Mail } from 'lucide-react';
import { useState } from 'react';

const InteriorDesignPage = () => {
  const [activeSlider, setActiveSlider] = useState(0);
  const [sliderPositions, setSliderPositions] = useState([50, 50, 50]);

  const beforeAfterImages = [
    {
      before: 'https://images.unsplash.com/photo-1484101403633-562f891dc89a?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      after: 'https://images.unsplash.com/photo-1709418440136-9c5c2900782d?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      title: 'Living Room Transformation',
      location: 'La Jolla, CA'
    },
    {
      before: 'https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      after: 'https://images.unsplash.com/photo-1648523962357-bf06f3002131?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      title: 'Master Bedroom Refresh',
      location: 'Del Mar, CA'
    },
    {
      before: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      after: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
      title: 'Kitchen Redesign',
      location: 'Encinitas, CA'
    }
  ];

  const galleryImages = [
    {
      url: 'https://images.unsplash.com/photo-1709418440136-9c5c2900782d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
      alt: 'Modern coastal living room'
    },
    {
      url: 'https://images.unsplash.com/photo-1648523962357-bf06f3002131?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
      alt: 'Serene bedroom design'
    },
    {
      url: 'https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
      alt: 'Bright kitchen'
    },
    {
      url: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
      alt: 'Minimalist bathroom'
    },
    {
      url: 'https://images.unsplash.com/photo-1505691938895-1758d7feb511?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
      alt: 'Cozy reading nook'
    },
    {
      url: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
      alt: 'Outdoor living space'
    }
  ];

  const handleSliderChange = (index, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    const newPositions = [...sliderPositions];
    newPositions[index] = Math.max(0, Math.min(100, percentage));
    setSliderPositions(newPositions);
  };

  return (
    <div data-testid="interior-design-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-[#F9F7F2]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <p className="font-sans text-sm uppercase tracking-[0.2em] text-[#D4A373] mb-6">
                Interior Design
              </p>
              <h1 className="font-serif text-5xl md:text-6xl text-[#2C3E50] mb-8 leading-tight">
                Create Your Sanctuary
              </h1>
              <p className="font-sans text-[#57534E] text-lg leading-relaxed mb-8">
                Your home should be a reflection of who you are—a place of calm, beauty, 
                and inspiration. Let's transform your spaces into the coastal retreat 
                you've always dreamed of.
              </p>
              <Link to="/contact">
                <Button
                  data-testid="interior-cta-btn"
                  className="rounded-full bg-[#2C3E50] hover:bg-[#1a252f] text-white px-8 py-6 text-base transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                >
                  Start Your Project
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] rounded-2xl overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_breeze-styling/artifacts/ms7yznlh_image.png"
                  alt="Coastal bedroom design"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Before & After Section */}
      <section data-testid="before-after-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="font-sans text-sm uppercase tracking-[0.2em] text-[#D4A373] mb-4">
              Transformations
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#2C3E50]">
              Before & After
            </h2>
          </div>

          <div className="space-y-16">
            {beforeAfterImages.map((item, index) => (
              <div key={index} data-testid={`before-after-${index}`} className="space-y-4">
                <div className="flex justify-between items-end mb-4">
                  <div>
                    <h3 className="font-serif text-2xl text-[#2C3E50]">{item.title}</h3>
                    <p className="font-sans text-[#D4A373] text-sm">{item.location}</p>
                  </div>
                  <div className="flex items-center gap-2 text-[#57534E] text-sm">
                    <ArrowLeftRight className="h-4 w-4" />
                    <span>Drag to compare</span>
                  </div>
                </div>
                
                {/* Before/After Slider */}
                <div 
                  className="relative aspect-[16/9] rounded-2xl overflow-hidden cursor-ew-resize select-none"
                  onMouseMove={(e) => handleSliderChange(index, e)}
                  onTouchMove={(e) => {
                    const touch = e.touches[0];
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = touch.clientX - rect.left;
                    const percentage = (x / rect.width) * 100;
                    const newPositions = [...sliderPositions];
                    newPositions[index] = Math.max(0, Math.min(100, percentage));
                    setSliderPositions(newPositions);
                  }}
                >
                  {/* After Image (Background) */}
                  <img
                    src={item.after}
                    alt={`${item.title} After`}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  
                  {/* Before Image (Clipped) */}
                  <div 
                    className="absolute inset-0 overflow-hidden"
                    style={{ width: `${sliderPositions[index]}%` }}
                  >
                    <img
                      src={item.before}
                      alt={`${item.title} Before`}
                      className="absolute inset-0 w-full h-full object-cover"
                      style={{ width: `${100 / (sliderPositions[index] / 100)}%`, maxWidth: 'none' }}
                    />
                  </div>
                  
                  {/* Slider Handle */}
                  <div 
                    className="absolute top-0 bottom-0 w-1 bg-white shadow-lg"
                    style={{ left: `${sliderPositions[index]}%`, transform: 'translateX(-50%)' }}
                  >
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center">
                      <ArrowLeftRight className="h-5 w-5 text-[#2C3E50]" />
                    </div>
                  </div>
                  
                  {/* Labels */}
                  <span className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-sans text-[#2C3E50]">
                    Before
                  </span>
                  <span className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-sans text-[#2C3E50]">
                    After
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section data-testid="interior-services" className="py-20 md:py-32 bg-[#F9F7F2]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="font-sans text-sm uppercase tracking-[0.2em] text-[#D4A373] mb-4">
            Services & Pricing
          </p>
          <h2 className="font-serif text-4xl md:text-5xl text-[#2C3E50] mb-8">
            Design Services
          </h2>
          <p className="font-sans text-[#57534E] text-lg leading-relaxed mb-6">
            From single-room refreshes to full home transformations, I offer personalized 
            interior design services tailored to your unique style and needs. Each project 
            is custom-scoped based on the space, timeline, and your vision.
          </p>
          <p className="font-sans text-[#57534E] text-lg leading-relaxed mb-10">
            For pricing information and to discuss your project, please reach out via email. 
            I'd love to learn about your space and how we can transform it together.
          </p>
          <a href="mailto:hello@maisonloulou.co">
            <Button
              data-testid="interior-email-btn"
              className="rounded-full bg-[#2C3E50] hover:bg-[#1a252f] text-white px-10 py-6 text-lg transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
            >
              <Mail className="mr-2 h-5 w-5" />
              hello@maisonloulou.co
            </Button>
          </a>
        </div>
      </section>

      {/* Gallery */}
      <section data-testid="interior-gallery" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="font-sans text-sm uppercase tracking-[0.2em] text-[#D4A373] mb-4">
              Portfolio
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#2C3E50]">
              Recent Projects
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {galleryImages.map((image, index) => (
              <div
                key={index}
                className={`rounded-2xl overflow-hidden ${
                  index === 0 ? 'md:col-span-2 md:row-span-2 aspect-square md:aspect-auto' : 'aspect-square'
                }`}
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Design Process */}
      <section className="py-20 md:py-32 bg-[#F9F7F2]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="font-sans text-sm uppercase tracking-[0.2em] text-[#D4A373] mb-4">
              The Journey
            </p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#2C3E50]">
              Design Process
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Discovery', desc: 'We discuss your vision, lifestyle needs, and design preferences during an initial consultation.' },
              { step: '02', title: 'Concept', desc: 'I create a design concept with mood boards, color palettes, and furniture selections.' },
              { step: '03', title: 'Design', desc: 'Detailed floor plans, 3D renderings, and a comprehensive design package are developed.' },
              { step: '04', title: 'Execution', desc: 'I oversee the implementation, ensuring every detail comes together beautifully.' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <span className="font-serif text-5xl text-[#D4A373]/30">{item.step}</span>
                <h3 className="font-serif text-xl text-[#2C3E50] mt-4 mb-3">{item.title}</h3>
                <p className="font-sans text-[#57534E] text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-32 bg-[#2C3E50] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl md:text-5xl mb-6">
            Let's Design Your Dream Space
          </h2>
          <p className="font-sans text-white/70 text-lg mb-10 max-w-2xl mx-auto">
            Ready to transform your home into a coastal haven? 
            Book a consultation and let's bring your vision to life.
          </p>
          <Link to="/contact">
            <Button
              data-testid="interior-final-cta-btn"
              className="rounded-full bg-[#D4A373] hover:bg-[#c29260] text-white px-10 py-6 text-lg transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
            >
              Start Your Project
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default InteriorDesignPage;
